import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { authAPI } from "../../services/api";
import {
  useLanguage,
  supportedLanguages,
} from "../../contexts/LanguageContext"; // 언어 Context 사용
import "./Header.css";

// 다국어 지원을 위한 번역 객체
const translations = {
  ko: {
    serviceName: "MyECRF",
    remainingTime: "남은 시간:",
    extendTime: "시간 연장",
    logout: "로그아웃",
    sessionWarningTitle: "세션 만료 알림",
    sessionWarningMessage: "로그인 세션이 15분 후에 만료됩니다.",
    sessionWarningAction: "계속 사용하시려면 세션을 연장해주세요.",
    extendSession: "세션 연장하기",
    close: "닫기",
    language: "언어",
    selectLanguage: "언어 선택",
  },
  en: {
    serviceName: "MyECRF",
    remainingTime: "Remaining time:",
    extendTime: "Extend Time",
    logout: "Logout",
    sessionWarningTitle: "Session Expiration Notice",
    sessionWarningMessage: "Your login session will expire in 15 minutes.",
    sessionWarningAction: "Please extend your session to continue.",
    extendSession: "Extend Session",
    close: "Close",
    language: "Language",
    selectLanguage: "Select Language",
  },
  ja: {
    serviceName: "MyECRF",
    remainingTime: "残り時間:",
    extendTime: "時間延長",
    logout: "ログアウト",
    sessionWarningTitle: "セッション有効期限のお知らせ",
    sessionWarningMessage: "ログインセッションは15分後に期限切れになります。",
    sessionWarningAction: "続けて使用するには、セッションを延長してください。",
    extendSession: "セッションを延長する",
    close: "閉じる",
    language: "言語",
    selectLanguage: "言語を選択",
  },
  "zh-CN": {
    serviceName: "MyECRF",
    remainingTime: "剩余时间:",
    extendTime: "延长时间",
    logout: "登出",
    sessionWarningTitle: "会话到期通知",
    sessionWarningMessage: "您的登录会话将在15分钟后到期。",
    sessionWarningAction: "请延长您的会话以继续。",
    extendSession: "延长会话",
    close: "关闭",
    language: "语言",
    selectLanguage: "选择语言",
  },
  "zh-TW": {
    serviceName: "MyECRF",
    remainingTime: "剩餘時間:",
    extendTime: "延長時間",
    logout: "登出",
    sessionWarningTitle: "會話到期通知",
    sessionWarningMessage: "您的登錄會話將在15分鐘後到期。",
    sessionWarningAction: "請延長您的會話以繼續。",
    extendSession: "延長會話",
    close: "關閉",
    language: "語言",
    selectLanguage: "選擇語言",
  },
  it: {
    serviceName: "MyECRF",
    remainingTime: "Tempo rimanente:",
    extendTime: "Estendi tempo",
    logout: "Esci",
    sessionWarningTitle: "Avviso di scadenza sessione",
    sessionWarningMessage: "La tua sessione scadrà tra 15 minuti.",
    sessionWarningAction: "Estendi la sessione per continuare.",
    extendSession: "Estendi sessione",
    close: "Chiudi",
    language: "Lingua",
    selectLanguage: "Seleziona lingua",
  },
  fr: {
    serviceName: "MyECRF",
    remainingTime: "Temps restant :",
    extendTime: "Prolonger",
    logout: "Déconnexion",
    sessionWarningTitle: "Avis d'expiration de session",
    sessionWarningMessage: "Votre session expirera dans 15 minutes.",
    sessionWarningAction: "Veuillez prolonger votre session pour continuer.",
    extendSession: "Prolonger la session",
    close: "Fermer",
    language: "Langue",
    selectLanguage: "Sélectionner la langue",
  },
  de: {
    serviceName: "MyECRF",
    remainingTime: "Verbleibende Zeit:",
    extendTime: "Zeit verlängern",
    logout: "Abmelden",
    sessionWarningTitle: "Hinweis zum Sitzungsablauf",
    sessionWarningMessage: "Ihre Sitzung läuft in 15 Minuten ab.",
    sessionWarningAction: "Bitte verlängern Sie Ihre Sitzung, um fortzufahren.",
    extendSession: "Sitzung verlängern",
    close: "Schließen",
    language: "Sprache",
    selectLanguage: "Sprache auswählen",
  },
  es: {
    serviceName: "MyECRF",
    remainingTime: "Tiempo restante:",
    extendTime: "Extender tiempo",
    logout: "Cerrar sesión",
    sessionWarningTitle: "Aviso de caducidad de sesión",
    sessionWarningMessage: "Su sesión caducará en 15 minutos.",
    sessionWarningAction: "Por favor, extienda su sesión para continuar.",
    extendSession: "Extender sesión",
    close: "Cerrar",
    language: "Idioma",
    selectLanguage: "Seleccionar idioma",
  },
  ru: {
    serviceName: "MyECRF",
    remainingTime: "Оставшееся время:",
    extendTime: "Продлить время",
    logout: "Выход",
    sessionWarningTitle: "Уведомление об истечении сессии",
    sessionWarningMessage: "Ваша сессия истечет через 15 минут.",
    sessionWarningAction: "Пожалуйста, продлите сессию, чтобы продолжить.",
    extendSession: "Продлить сессию",
    close: "Закрыть",
    language: "Язык",
    selectLanguage: "Выберите язык",
  },
  pt: {
    serviceName: "MyECRF",
    remainingTime: "Tempo restante:",
    extendTime: "Estender tempo",
    logout: "Sair",
    sessionWarningTitle: "Aviso de expiração de sessão",
    sessionWarningMessage: "Sua sessão expirará em 15 minutos.",
    sessionWarningAction: "Por favor, estenda sua sessão para continuar.",
    extendSession: "Estender sessão",
    close: "Fechar",
    language: "Idioma",
    selectLanguage: "Selecionar idioma",
  },
  ar: {
    serviceName: "MyECRF",
    remainingTime: "الوقت المتبقي:",
    extendTime: "تمديد الوقت",
    logout: "تسجيل الخروج",
    sessionWarningTitle: "إشعار انتهاء الجلسة",
    sessionWarningMessage: "ستنتهي جلستك في غضون 15 دقيقة.",
    sessionWarningAction: "يرجى تمديد جلستك للمتابعة.",
    extendSession: "تمديد الجلسة",
    close: "إغلاق",
    language: "اللغة",
    selectLanguage: "اختر اللغة",
  },
  hi: {
    serviceName: "MyECRF",
    remainingTime: "शेष समय:",
    extendTime: "समय बढ़ाएँ",
    logout: "लॉगआउट",
    sessionWarningTitle: "सत्र समाप्ति सूचना",
    sessionWarningMessage: "आपका लॉगिन सत्र 15 मिनट में समाप्त हो जाएगा।",
    sessionWarningAction: "जारी रखने के लिए कृपया अपने सत्र को बढ़ाएँ।",
    extendSession: "सत्र बढ़ाएँ",
    close: "बंद करें",
    language: "भाषा",
    selectLanguage: "भाषा चुनें",
  },
};

// 언어 옵션 리스트는 LanguageContext에서 import하므로 제거

const Header = () => {
  const navigate = useNavigate();
  const [remainingTime, setRemainingTime] = useState(10800); // 3시간 (3600 * 3)
  const [username, setUsername] = useState("");
  const [showWarning, setShowWarning] = useState(false); // 경고 팝업 표시 여부
  const [showLanguageDropdown, setShowLanguageDropdown] = useState(false);

  // 언어 Context 사용
  const { currentLanguage, changeLanguage } = useLanguage();

  useEffect(() => {
    // 사용자 정보 로드
    const token = localStorage.getItem("token");
    if (token) {
      fetchUserInfo();
    }

    // 남은 시간 타이머
    const timer = setInterval(() => {
      setRemainingTime((prev) => {
        // 15분(900초) 전에 경고 표시
        if (prev === 900) {
          setShowWarning(true);
        }

        if (prev <= 0) {
          handleLogout();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  // 현재 언어에 따른 텍스트 가져오기
  const getText = (key) => {
    return translations[currentLanguage]?.[key] || translations["en"][key];
  };

  const fetchUserInfo = async () => {
    try {
      const data = await authAPI.getUserInfo();
      setUsername(data.user?.name || "");
    } catch (error) {
      console.error("사용자 정보 로드 실패:", error);
    }
  };

  // 시간 연장 - API 호출 없이 바로 3시간으로 설정
  const handleExtendTime = () => {
    setRemainingTime(10800); // 3시간으로 시간 연장
    setShowWarning(false); // 경고 팝업 닫기
  };

  const handleLogout = async () => {
    try {
      console.log("로그아웃 시작...");
      const result = await authAPI.logout();
      console.log("로그아웃 결과:", result);

      // 로그아웃 성공 시 로그인 페이지로 이동
      navigate("/login");
    } catch (error) {
      console.error("로그아웃 처리 중 오류:", error);
      // 오류가 발생해도 로그인 페이지로 이동
      localStorage.removeItem("token");
      navigate("/login");
    }
  };

  // 경고 팝업 닫기
  const closeWarning = () => {
    setShowWarning(false);
  };

  const formatTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(
      2,
      "0"
    )}:${String(secs).padStart(2, "0")}`;
  };

  // 언어 변경 핸들러 - Context의 changeLanguage 사용
  const handleLanguageChange = (langCode) => {
    changeLanguage(langCode);
    setShowLanguageDropdown(false);
  };

  // 언어 드롭다운 토글
  const toggleLanguageDropdown = () => {
    setShowLanguageDropdown(!showLanguageDropdown);
  };

  // 현재 선택된 언어 이름 가져오기
  const getCurrentLanguageName = () => {
    return (
      supportedLanguages.find((lang) => lang.code === currentLanguage)?.name ||
      "Language"
    );
  };

  return (
    <header className="main-header">
      <div className="header-left">
        <h1 className="logo">{getText("serviceName")}</h1>
      </div>
      <div className="header-center"></div>
      <div className="header-right">
        <div className="language-selector">
          <button className="language-button" onClick={toggleLanguageDropdown}>
            {getCurrentLanguageName()} ▼
          </button>
          {showLanguageDropdown && (
            <div className="language-dropdown">
              {supportedLanguages.map((lang) => (
                <div
                  key={lang.code}
                  className={`language-option ${
                    currentLanguage === lang.code ? "selected" : ""
                  }`}
                  onClick={() => handleLanguageChange(lang.code)}
                >
                  {lang.name}
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="session-time-container">
          <span className="session-time-display">
            {getText("remainingTime")} {formatTime(remainingTime)}
          </span>
          <button className="extend-time-button" onClick={handleExtendTime}>
            {getText("extendTime")}
          </button>
        </div>
        <div className="user-info">
          <span className="username">{username}</span>
          <button className="logout-button" onClick={handleLogout}>
            {getText("logout")}
          </button>
        </div>
      </div>

      {/* 세션 만료 경고 팝업 */}
      {showWarning && (
        <div className="session-warning-overlay">
          <div className="session-warning-popup">
            <h3>{getText("sessionWarningTitle")}</h3>
            <p>{getText("sessionWarningMessage")}</p>
            <p>{getText("sessionWarningAction")}</p>
            <div className="warning-buttons">
              <button className="extend-session-btn" onClick={handleExtendTime}>
                {getText("extendSession")}
              </button>
              <button className="close-warning-btn" onClick={closeWarning}>
                {getText("close")}
              </button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
